import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AppViewMode } from "../domain/contracts";

type RouteSelection = {
  viewRef: AppViewMode | null;
  villageRef: string | null;
  intervieweeRef: string | null;
};

export type RouteSelectionController = {
  viewRef: AppViewMode | null;
  villageRef: string | null;
  intervieweeRef: string | null;
  setViewRef: (nextViewRef: AppViewMode | null) => void;
  setVillageRef: (nextVillageRef: string | null) => void;
  setIntervieweeRef: (nextIntervieweeRef: string | null) => void;
};

const VALID_VIEW_MODES: readonly AppViewMode[] = [
  "map",
  "village",
  "interviewee",
  "villagesList",
  "intervieweesList",
] as const;

function parseViewParam(raw: string | null): AppViewMode | null {
  if (raw && (VALID_VIEW_MODES as readonly string[]).includes(raw)) {
    return raw as AppViewMode;
  }
  return null;
}

function inferViewFromLegacyParams(
  villageRef: string | null,
  intervieweeRef: string | null,
): AppViewMode | null {
  if (intervieweeRef) return "interviewee";
  if (villageRef) return "village";
  return null;
}

function readRouteSelection(): RouteSelection {
  const params = new URLSearchParams(window.location.search);
  const villageRef = params.get("village");
  const intervieweeRef = params.get("interviewee");
  const viewRef = parseViewParam(params.get("view")) ?? inferViewFromLegacyParams(villageRef, intervieweeRef);

  return {
    viewRef,
    villageRef,
    intervieweeRef,
  };
}

function writeRouteSelection(
  selection: RouteSelection,
  mode: "replace" | "push",
  extraState?: Record<string, unknown>,
): void {
  const url = new URL(window.location.href);

  if (selection.viewRef && selection.viewRef !== "map") {
    url.searchParams.set("view", selection.viewRef);
  } else {
    url.searchParams.delete("view");
  }

  if (selection.villageRef) {
    url.searchParams.set("village", selection.villageRef);
  } else {
    url.searchParams.delete("village");
  }

  if (selection.intervieweeRef) {
    url.searchParams.set("interviewee", selection.intervieweeRef);
  } else {
    url.searchParams.delete("interviewee");
  }

  const nextHref = `${url.pathname}${url.search}${url.hash}`;
  const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextHref === currentHref) {
    return;
  }

  const state = extraState ?? {};

  if (mode === "replace") {
    window.history.replaceState(state, "", url);
  } else {
    window.history.pushState(state, "", url);
  }
}

export function useRouteSelection() {
  const [selection, setSelection] = useState<RouteSelection>(() => readRouteSelection());
  const hasWrittenInitialSelection = useRef(false);

  useEffect(() => {
    function handlePopState() {
      setSelection(readRouteSelection());
    }

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    writeRouteSelection(selection, hasWrittenInitialSelection.current ? "push" : "replace");
    hasWrittenInitialSelection.current = true;
  }, [selection]);

  const setViewRef = useCallback((nextViewRef: AppViewMode | null) => {
    setSelection((current) => {
      if (current.viewRef === nextViewRef) {
        return current;
      }

      // "map" and listing views don't need village/interviewee params,
      // but they DO need their viewRef preserved so the UI renders the correct view.
      const isMapOrNull = !nextViewRef || nextViewRef === "map";
      const shouldClearSelection =
        isMapOrNull || nextViewRef === "villagesList" || nextViewRef === "intervieweesList";

      return {
        viewRef: isMapOrNull ? null : nextViewRef,
        villageRef: shouldClearSelection ? null : current.villageRef,
        intervieweeRef: shouldClearSelection ? null : current.intervieweeRef,
      };
    });
  }, []);

  const setVillageRef = useCallback((nextVillageRef: string | null) => {
    setSelection((current) => {
      if (current.villageRef === nextVillageRef) {
        return current;
      }

      return {
        ...current,
        villageRef: nextVillageRef,
        intervieweeRef: null,
      };
    });
  }, []);

  const setIntervieweeRef = useCallback((nextIntervieweeRef: string | null) => {
    setSelection((current) => {
      if (current.intervieweeRef === nextIntervieweeRef) {
        return current;
      }

      return {
        ...current,
        intervieweeRef: nextIntervieweeRef,
      };
    });
  }, []);

  return useMemo<RouteSelectionController>(
    () => ({
      viewRef: selection.viewRef,
      villageRef: selection.villageRef,
      intervieweeRef: selection.intervieweeRef,
      setViewRef,
      setVillageRef,
      setIntervieweeRef,
    }),
    [selection.viewRef, selection.villageRef, selection.intervieweeRef, setViewRef, setVillageRef, setIntervieweeRef],
  );
}